import { filter, remove } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import { IDashboardChartData, IDashboardChartDataKey } from '~/src/dash/api/dashboards/home';
import { IDashboardSubChartMetric } from '~/src/dash/components/charts/common';
import { MultiLineChart } from '~/src/dash/components/charts/MultiLineChart';
import { Row, Rows, spacing, Text, TKey, useIsMounted } from '~/src/dash/publicaUI';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { IActiveFilters } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { DashboardSubchartMetricToggleRow } from '~/src/dash/views/dashboards/DashboardHome/sections/DashboardLive/DashboardSubChart/DashboardSubchartMetricToggles';

const extractActiveMetrics = (
  cfg: Record<string, IDashboardSubChartMetric>,
  activeFilters: IActiveFilters,
) => {
  return filter(Object.values(cfg), (metric) => {
    return !metric.isDisabled(activeFilters);
  });
};

interface IDashboardSubChartProps {
  title: string;
  data: Partial<IDashboardChartData>[];
  loadState: LoadingState;
  metricsCfg: Record<string, IDashboardSubChartMetric>;
  orderedMetricKeys: IDashboardChartDataKey[];
  activeFilters: IActiveFilters;
  syncID?: string;
  tKey?: TKey;
}

export const DashboardSubChart = ({
  title,
  tKey,
  data = [],
  loadState,
  metricsCfg,
  orderedMetricKeys,
  activeFilters,
  syncID,
}: IDashboardSubChartProps) => {
  const isMounted = useIsMounted();
  const [activeMetrics, setActiveMetrics] = useState(
    extractActiveMetrics(metricsCfg, activeFilters),
  );

  useEffect(() => {
    if (isMounted()) {
      setActiveMetrics(extractActiveMetrics(metricsCfg, activeFilters));
    }
  }, [activeFilters]);

  const handleChangeActiveMetric = (metricKey: string, isActive: boolean) => {
    const clonedActiveMetrics = [...activeMetrics];
    if (isActive) {
      remove(clonedActiveMetrics, { key: metricKey });
    } else {
      clonedActiveMetrics.push(metricsCfg[metricKey]);
    }
    setActiveMetrics(clonedActiveMetrics);
  };

  return (
    <Rows>
      <Row style={{ padding: `${spacing.small} ${spacing.base} 0` }}>
        <Text small bold tKey={tKey}>
          {title}
        </Text>
      </Row>
      <Row smallVerticalPadding>
        <DashboardSubchartMetricToggleRow
          metricsCfg={metricsCfg}
          orderedMetricKeys={orderedMetricKeys}
          activeMetrics={activeMetrics}
          handleChangeActiveMetric={handleChangeActiveMetric}
          activeFilters={activeFilters}
        />
      </Row>
      <Row>
        <MultiLineChart
          loadState={loadState}
          data={data}
          metrics={activeMetrics}
          metricsCfg={metricsCfg}
          syncID={syncID}
        />
      </Row>
    </Rows>
  );
};
